import { useEffect, useState } from "react";
import { api, formatApiError, uploadFiles } from "@/lib/api";
import { toast } from "sonner";
import { usePhotoInputs, PhotoUploadButton } from "@/components/shared/PhotoPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { plur } from "@/lib/format";

// Nahrání fotek rovnou z Galerie. Zakázky se načítají z /projects (ne z fotek),
// aby šla nahrát i první fotka zakázky a fungovalo to při prázdném „Dnes".
export function GalleryUploadDialog({ open, onClose, defaultProject, defaultDate, onDone }) {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [date, setDate] = useState("");
  const [caption, setCaption] = useState("");
  const [prog, setProg] = useState(null);
  const photoPick = usePhotoInputs({ onChange: (e) => handleFiles(e), testId: "upload-file-input" });

  useEffect(() => {
    if (!open) return;
    setPid(defaultProject || "");
    setDate(defaultDate || new Date().toISOString().slice(0, 10));
    setCaption("");
    setProg(null);
    api.get("/projects/options").then((r) => setProjects(Array.isArray(r.data) ? r.data : r.data.projects || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open, defaultProject, defaultDate]);

  // Každá fotka zvlášť — jedno selhání nezahodí už nahrané (vzor z deníku).
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setProg({ done: 0, total: files.length });
    // 216: až tři fotky naráz, pořadí podle výběru, chyba jedné nezahodí ostatní.
    const res = await uploadFiles(files, { onEach: (done) => setProg((p) => (p ? { ...p, done } : p)) });
    const urls = res.urls;
    const failed = res.failed.map(({ file, error }) => ({ name: file.name || "fotka", why: formatApiError(error.response?.data?.detail) }));
    try {
      if (urls.length) await api.post(`/projects/${pid}/photos`, { urls, date, caption: caption.trim() });
      if (!failed.length) toast.success(`Přidáno: ${plur(urls.length, "fotka", "fotky", "fotek")}.`);
      else toast.error(`Nahráno ${urls.length} z ${files.length}. Nepovedlo se: ${failed.map((x) => x.name).join(", ")} — ${failed[0].why}`, { duration: 12000 });
      if (urls.length) { onDone(); onClose(); }
    } catch (e3) { toast.error(formatApiError(e3.response?.data?.detail)); }
    setProg(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !prog && onClose()}>
      <DialogContent sheet className="sm:max-w-md max-md:pt-8" data-testid="gallery-upload-dialog">
        <DialogHeader><DialogTitle>Nahrát fotky</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold">Zakázka</label>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger className="h-11" data-testid="upload-project"><SelectValue placeholder="Vyber zakázku…" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`upload-project-${p.id}`}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-semibold">Datum</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11" data-testid="upload-date" />
            <p className="text-xs text-muted-foreground">Fotka z minulého týdne patří k tomu dni, ne ke dni nahrání.</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-semibold">Popisek (nepovinný, platí pro všechny fotky)</label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Např. základová deska" className="h-11" data-testid="upload-caption" />
          </div>
          {photoPick.inputs}
          {prog ? (
            <div className="text-sm font-bold text-brand" data-testid="upload-progress">Nahrávám {prog.done}/{prog.total}…</div>
          ) : (
            <PhotoUploadButton onClick={photoPick.open} disabled={!pid || !date} testid="upload-pick" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
